import React, { useState, memo, useCallback, useEffect } from "react";
import { withCustomAuthenticator } from "../components/customAuthenticator";
import Navbar from "../components/Navbar";
import { Helmet } from "react-helmet";
import "./Account.css";
import {
  updateUserAttributes,
  updatePassword,
  fetchAuthSession,
} from "aws-amplify/auth";
import { Alert, Snackbar } from "@mui/material";
import FetchHistory from "../components/FetchHistory";
import ConfirmDialog from "../components/ConfirmDialog";

// Add validation helper function at the top level
const validateField = (name, value) => {
  switch (name) {
    case "given_name":
    case "family_name":
      return value.trim().length >= 2 ? "" : "Must be at least 2 characters";
    case "phone_number":
      return /^\+?[\d\s-]{10,}$/.test(value)
        ? ""
        : "Please enter a valid phone number";
    default:
      return "";
  }
};

// Move ProfileSection outside of Account component
const ProfileSection = memo(({ user, setUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    given_name: user.given_name || "",
    family_name: user.family_name || "",
    email: user.email || "",
    phone_number: user.phone_number || "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    given_name: "",
    family_name: "",
    phone_number: "",
  });

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate field on change
    setValidationErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  }, []);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setIsSubmitting(true);

    try {
      await updateUserAttributes({
        userAttributes: {
          given_name: formData.given_name,
          family_name: formData.family_name,
          phone_number: formData.phone_number,
        },
      });

      setSuccessMessage("Profile updated successfully!");
      setIsEditing(false);
      setUser({ ...user, ...formData });
      localStorage.setItem(
        "session",
        JSON.stringify({
          ...JSON.parse(localStorage.getItem("session")),
          tokens: {
            ...JSON.parse(localStorage.getItem("session")).tokens,
            idToken: {
              ...JSON.parse(localStorage.getItem("session")).tokens.idToken,
              payload: { ...user, ...formData },
            },
          },
        })
      );
    } catch (error) {
      console.error("Update error:", error);
      setErrorMessage(error.message || "Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      given_name: user.given_name || "",
      family_name: user.family_name || "",
      email: user.email || "",
      phone_number: user.phone_number || "",
    });
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (isEditing && !Object.values(validationErrors).some((error) => error)) {
      handleUpdateProfile(e);
    }
  };

  const renderForm = () => (
    <form onSubmit={handleFormSubmit} className="profile-form" noValidate>
      <div
        className={`form-group ${
          validationErrors.given_name ? "has-error" : ""
        }`}
      >
        <label>First Name</label>
        <input
          type="text"
          name="given_name"
          value={formData.given_name}
          onChange={handleInputChange}
          disabled={!isEditing || isSubmitting}
        />
        {validationErrors.given_name && (
          <span className="error-text">{validationErrors.given_name}</span>
        )}
      </div>

      <div
        className={`form-group ${
          validationErrors.family_name ? "has-error" : ""
        }`}
      >
        <label>Last Name</label>
        <input
          type="text"
          name="family_name"
          value={formData.family_name}
          onChange={handleInputChange}
          disabled={!isEditing || isSubmitting}
        />
        {validationErrors.family_name && (
          <span className="error-text">{validationErrors.family_name}</span>
        )}
      </div>

      <div className="form-group">
        <label>Email</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          disabled={true}
        />
      </div>

      <div
        className={`form-group ${
          validationErrors.phone_number ? "has-error" : ""
        }`}
      >
        <label>Phone Number</label>
        <input
          type="tel"
          name="phone_number"
          value={formData.phone_number}
          onChange={handleInputChange}
          disabled={!isEditing || isSubmitting}
          placeholder="+1234567890"
        />
        {validationErrors.phone_number && (
          <span className="error-text">{validationErrors.phone_number}</span>
        )}
      </div>

      <div className="button-group">
        {!isEditing ? (
          <button
            type="button"
            className="edit-button"
            onClick={(e) => {
              e.preventDefault();
              setIsEditing(true);
            }}
            disabled={isSubmitting}
          >
            Edit Profile
          </button>
        ) : (
          <>
            <button
              type="submit"
              className="save-button"
              disabled={
                isSubmitting ||
                Object.values(validationErrors).some((error) => error)
              }
            >
              {isSubmitting ? (
                <span className="loading-spinner">Saving...</span>
              ) : (
                "Save Changes"
              )}
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={(e) => {
                e.preventDefault();
                handleCancel();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </form>
  );

  return (
    <div className="profile-section">
      <h2>Profile Information</h2>
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
      {renderForm()}
    </div>
  );
});

// Add SecuritySection component outside Account component
const SecuritySection = memo(({ user }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState(null);

  const validatePasswordField = (name, value) => {
    switch (name) {
      case "currentPassword":
        return value.length < 8 ? "Current password is required" : "";
      case "newPassword":
        if (value.length < 8) return "Password must be at least 8 characters";
        if (!/[A-Z]/.test(value))
          return "Password must contain an uppercase letter";
        if (!/[a-z]/.test(value))
          return "Password must contain a lowercase letter";
        if (!/[0-9]/.test(value)) return "Password must contain a number";
        if (!/[^A-Za-z0-9]/.test(value))
          return "Password must contain a special character";
        return "";
      case "confirmPassword":
        return value !== newPassword ? "Passwords do not match" : "";
      default:
        return "";
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setIsSubmitting(true);

    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setSuccessMessage("Password updated successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Password update error:", error);
      setErrorMessage(error.message || "Failed to update password");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case "currentPassword":
        setCurrentPassword(value);
        break;
      case "newPassword":
        setNewPassword(value);
        break;
      case "confirmPassword":
        setConfirmPassword(value);
        break;
      default:
        break;
    }

    setValidationErrors((prev) => ({
      ...prev,
      [name]: validatePasswordField(name, value),
    }));
  };

  const handleAction = async (action) => {
    setDialogAction(action);
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    setDialogOpen(false);
    if (dialogAction === "delete") {
      setIsDeleting(true);
      setDeleteErrorMessage("");
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/deleteUserData`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete user data");
        }
        if (response.status === 200) {
          setDeleteSuccessMessage("Successfully removed all data.");
        } else {
          throw new Error("Failed to delete user data");
        }
      } catch (error) {
        console.error("Error deleting user data:", error);
        setDeleteErrorMessage("Failed to delete user data");
      } finally {
        setIsDeleting(false);
      }

      setIsDeleting(false);
    }
  };

  return (
    <div className="security-section">
      <h2>Security Settings</h2>
      <div className="security-card">
        <h3>Change Password</h3>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}

        <form
          onSubmit={handlePasswordChange}
          className="password-form"
          noValidate
        >
          <div
            className={`form-group ${
              validationErrors.currentPassword ? "has-error" : ""
            }`}
          >
            <label>Current Password</label>
            <input
              type="password"
              name="currentPassword"
              value={currentPassword}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
            {validationErrors.currentPassword && (
              <span className="error-text">
                {validationErrors.currentPassword}
              </span>
            )}
          </div>

          <div
            className={`form-group ${
              validationErrors.newPassword ? "has-error" : ""
            }`}
          >
            <label>New Password</label>
            <input
              type="password"
              name="newPassword"
              value={newPassword}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
            {validationErrors.newPassword && (
              <span className="error-text">{validationErrors.newPassword}</span>
            )}
          </div>

          <div
            className={`form-group ${
              validationErrors.confirmPassword ? "has-error" : ""
            }`}
          >
            <label>Confirm New Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={confirmPassword}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
            {validationErrors.confirmPassword && (
              <span className="error-text">
                {validationErrors.confirmPassword}
              </span>
            )}
          </div>

          <div className="button-group">
            <button
              type="submit"
              className="save-button"
              disabled={
                isSubmitting ||
                Object.values(validationErrors).some((error) => error) ||
                !currentPassword ||
                !newPassword ||
                !confirmPassword
              }
            >
              {isSubmitting ? (
                <span className="loading-spinner">Updating...</span>
              ) : (
                "Update Password"
              )}
            </button>
          </div>
        </form>
      </div>
      <div className="security-card">
        <h3>Two-Factor Authentication</h3>
        <p className="coming-soon">
          Two-factor authentication options coming soon!
        </p>
      </div>
      <div className="security-card">
        <h3>Delete My Data</h3>
        <p>
          Coin Basis honors your right to data privacy. Clicking the button
          below will remove all uploaded data and results from our servers. This
          action is irreversible.
        </p>
        <p>Note: This does not delete your account, only your uploaded data.</p>
        {deleteErrorMessage && (
          <div className="error-message">{deleteErrorMessage}</div>
        )}
        {deleteSuccessMessage && (
          <div className="success-message">{deleteSuccessMessage}</div>
        )}

        <ConfirmDialog
          open={dialogOpen}
          title="Confirm Action"
          message={
            "Are you sure you want to permanently delete all your uploaded data? This cannot be undone."
          }
          onConfirm={handleConfirm}
          onCancel={() => setDialogOpen(false)}
        />
        <button
          className="cancel-button"
          onClick={() => handleAction("delete")}
          disabled={isDeleting}
        >
          {isDeleting ? "Deleting..." : "Delete My Data"}
        </button>
      </div>
    </div>
  );
});

// Add this component outside Account component
const MembershipSection = memo(({ user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPlan, setCurrentPlan] = useState("Free");
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [cancelAtPeriod, setCancelAtPeriod] = useState(false);
  const [periodEnd, setPeriodEnd] = useState(null);

  useEffect(() => {
    fetchCurrentPlan();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.sub]);

  const fetchCurrentPlan = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/getSubscriptionStatus`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch subscription status");
      }

      const data = await response.json();
      setCurrentPlan(data.planType === "pro" ? "Pro" : "Free");
      setCancelAtPeriod(data.cancelAtPeriodEnd);
      setPeriodEnd(data.currentPeriodEnd);
      setErrorMessage("");
    } catch (error) {
      console.error("Error fetching subscription:", error);
      setErrorMessage("Failed to load subscription status");
    } finally {
      setIsLoadingPlan(false);
    }
  };

  const handleAction = async (action) => {
    setDialogAction(action);
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    setDialogOpen(false);
    setIsLoading(true);
    setErrorMessage("");

    try {
      const baseUrl = window.location.origin;
      let response;
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();

      if (dialogAction === "upgrade") {
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/createCheckoutSession`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              userId: user.sub,
              email: user.email,
              baseUrl,
            }),
          }
        );
      } else {
        // Handle downgrade/renew by updating subscription
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/updateSubscription`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              action: dialogAction,
            }),
          }
        );
      }

      if (!response.ok) {
        throw new Error("Failed to process request");
      }

      const data = await response.json();

      if (data.sessionUrl) {
        window.location.href = data.sessionUrl;
      } else {
        setAlertOpen(true);
        setSuccessMessage(data.message);
        fetchCurrentPlan();
      }
    } catch (error) {
      console.error("Error:", error);
      setErrorMessage(
        "Unable to process your request. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getConfirmationMessage = (action) => {
    switch (action) {
      case "upgrade":
        return "Are you sure you want to upgrade to the Pro plan? You'll be redirected to complete your subscription.";
      case "downgrade":
        return "Are you sure you want to cancel your Pro subscription? You'll still have access to Pro features until the end of your billing period.";
      case "renew":
        return "Would you like to continue your Pro subscription? Your subscription will be renewed at the end of the current period.";
      default:
        return "";
    }
  };

  const renderPlans = () => {
    return (
      <div className="plans-container">
        <div className={`plan-card ${currentPlan === "Free" ? "current" : ""}`}>
          {currentPlan === "Free" && (
            <div className="current-badge">Current Plan</div>
          )}
          <h3>Free Plan</h3>
          <div className="plan-price">
            $0<span className="price-period">/ year</span>
          </div>
          <ul className="plan-features">
            <li>Up to 3 transaction summaries per year</li>
            <li>Access to Coin Basis Portfolio</li>
            <li>Standard Email Support </li>
            <li className="excluded">Downloadable Tax Summary Forms</li>
          </ul>
          {currentPlan === "Pro" && !cancelAtPeriod && (
            <button
              className="downgrade-button"
              onClick={() => handleAction("downgrade")}
              disabled={isLoading}
            >
              {isLoading ? "Processing..." : "Downgrade to Free"}
            </button>
          )}
        </div>

        <div className={`plan-card ${currentPlan === "Pro" ? "current" : ""}`}>
          {currentPlan === "Pro" && (
            <div className="current-badge">Current Plan</div>
          )}
          <h3>Pro Plan</h3>
          <div className="plan-price">
            $20<span className="price-period">/ year</span>
          </div>
          <ul className="plan-features">
            <li>Unlimited transactions</li>
            <li>Access to Coin Basis Portfolio</li>
            <li>Priority Email Support</li>
            <li>Downloadable Tax Summary Forms</li>
            <li>Early access to new features</li>
          </ul>
          {currentPlan === "Free" ? (
            <button
              className="upgrade-button"
              onClick={() => handleAction("upgrade")}
              disabled={isLoading}
            >
              {isLoading ? "Processing..." : "Upgrade to Pro"}
            </button>
          ) : cancelAtPeriod ? (
            <button
              className="renew-button"
              onClick={() => handleAction("renew")}
              disabled={isLoading}
            >
              {isLoading ? "Processing..." : "Renew Subscription"}
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const formattedDate = periodEnd
    ? new Date(periodEnd * 1000).toLocaleDateString()
    : null;

  // useEffect(() => {
  //   const script = document.createElement("script");
  //   script.innerHTML = `
  //     document.getElementById("membershipButton").addEventListener("click", function() {
  //       gtag("event", "button_click", {
  //         event_category: "User Interaction",
  //         event_label: "Membership Button"
  //       });
  //     });
  //   `;
  //   document.body.appendChild(script);
  // }, []);

  return (
    <div className="membership-section">
      <h2>Membership</h2>
      {currentPlan === "Pro" && formattedDate && (
        <div
          className={`subscription-status ${
            cancelAtPeriod ? "canceling" : "active"
          }`}
        >
          Your Pro membership is set to {cancelAtPeriod ? "cancel" : "renew"} on{" "}
          {formattedDate}
        </div>
      )}

      {errorMessage && <div className="error-message">{errorMessage}</div>}

      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setAlertOpen(false)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={dialogOpen}
        title="Confirm Action"
        message={getConfirmationMessage(dialogAction)}
        onConfirm={handleConfirm}
        onCancel={() => setDialogOpen(false)}
      />

      {isLoadingPlan ? (
        <div className="loading-spinner">Loading plan details...</div>
      ) : (
        renderPlans()
      )}
    </div>
  );
});

const UploadHistorySection = memo(({ user, setSection }) => {
  const [uploadHistory, setUploadHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState("Free");

  useEffect(() => {
    const fetchUploadHistory = async () => {
      setIsLoading(true);
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/getUploadHistory`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch upload history");
        }
        const data = await response.json();
        setUploadHistory(data.folders); // Assuming the API returns an array of keys
      } catch (error) {
        console.error("Error fetching upload history:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCurrentPlan();
    fetchUploadHistory();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.sub]);

  const fetchCurrentPlan = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/getSubscriptionStatus`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch subscription status");
      }

      const data = await response.json();
      setCurrentPlan(data.planType === "pro" ? "Pro" : "Free");
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };

  return (
    <div className="upload-history-section">
      <h2>Upload History</h2>
      {isLoading ? (
        <p>Loading upload history...</p>
      ) : (
        <table className="upload-history-table">
          <thead>
            <tr>
              <th>Upload Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {uploadHistory.map((key) => (
              <FetchHistory
                key={key}
                prefix={key}
                setSection={setSection}
                currentPlan={currentPlan}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
});

const Account = () => {
  const [activeSection, setActiveSection] = useState(() => {
    // Get section from URL parameters
    const params = new URLSearchParams(window.location.search);
    const section = params.get("section");
    // Return valid section or default to "profile"
    return ["profile", "security", "membership", "upload-history"].includes(
      section
    )
      ? section
      : "profile";
  });

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    const session = await fetchAuthSession();
    const user = session.tokens.idToken.payload;
    setUser(user);
    setLoading(false);
  };

  useEffect(() => {
    fetchUser();
  }, []);

  // Update URL when section changes
  useEffect(() => {
    const url = new URL(window.location);
    url.searchParams.set("section", activeSection);
    window.history.pushState({}, "", url);
  }, [activeSection]);

  // useEffect(() => {
  //   const script = document.createElement("script");
  //   script.innerHTML = `
  //     document.getElementById("accountButton").addEventListener("click", function() {
  //       gtag("event", "button_click", {
  //         event_category: "User Interaction",
  //         event_label: "Click Me Button"
  //       });
  //     });
  //   `;
  //   document.body.appendChild(script);
  // }, []);

  const renderSection = () => {
    if (loading) {
      return <div>Loading...</div>;
    }
    switch (activeSection) {
      case "profile":
        return <ProfileSection user={user} setUser={setUser} />;
      case "security":
        return <SecuritySection user={user} />;
      case "membership":
        return <MembershipSection user={user} />;
      case "upload-history":
        return (
          <UploadHistorySection user={user} setSection={setActiveSection} />
        );
      default:
        return <ProfileSection />;
    }
  };

  return (
    <div className="page-container">
      <Helmet>
        <title>Coin Basis: Account</title>
        <meta property="og:title" content="Account" />
        {/* Google Analytics */}
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-1T6SHZQ9X8"
        ></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-1T6SHZQ9X8');
          `}
        </script>
        <script>
          {`
            (function(w,d,s,l,i){
              w[l]=w[l]||[];
              w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
              var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
              j.async=true;
              j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
              f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-MTZT7D3V');
          `}
        </script>
      </Helmet>
      {/* Google Tag Manager (noscript) */}
      <noscript>
        <iframe
          title="account-analytics"
          src="https://www.googletagmanager.com/ns.html?id=GTM-MTZT7D3V"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        ></iframe>
      </noscript>
      {/* End Google Tag Manager (noscript) */}
      <Navbar />
      <div className="main-container">
        <div className="content-container">
          <div className="sections-container">
            <div
              className={`section-header ${
                activeSection === "profile" ? "active" : ""
              }`}
              onClick={() => setActiveSection("profile")}
            >
              Profile
            </div>
            <div
              className={`section-header ${
                activeSection === "security" ? "active" : ""
              }`}
              onClick={() => setActiveSection("security")}
            >
              Security
            </div>
            <div
              className={`section-header ${
                activeSection === "membership" ? "active" : ""
              }`}
              onClick={() => setActiveSection("membership")}
            >
              Membership
            </div>
            <div
              className={`section-header ${
                activeSection === "upload-history" ? "active" : ""
              }`}
              onClick={() => setActiveSection("upload-history")}
            >
              Upload History
            </div>
          </div>
          <div className="results-container">{renderSection()}</div>
        </div>
      </div>
    </div>
  );
};

export default withCustomAuthenticator(Account);
